/**
 * PayoutsTab - Admin payouts management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { 
  Banknote, RefreshCw, CheckCircle, Play, Download, TrendingUp 
} from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function PayoutsTab({ 
  payouts,
  merchantBalances,
  stats,
  onPayoutAction,
  onExportPayout,
  onOpenPayoutDialog,
  getStatusIcon,
  getStatusColor,
  formatDate
}) {
  const { t } = useLanguage();

  return (
    <motion.div 
      key="payouts-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-4"
    >
      {/* Merchant Settlements Summary */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="merchant-balances">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            {t('payouts.merchantSettlements')}
          </h3>
        </div>
        {merchantBalances.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            <p>{t('tables.noSettlementData')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.colmado')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('payouts.settlementAmount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.totalReceived')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.totalPaidOut')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.pendingPayout')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {merchantBalances.map((mb) => (
                  <tr key={mb.merchant_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{mb.merchant_name}</td>
                    <td className="px-6 py-4 font-semibold text-emerald-600">${(mb.current_balance || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-slate-600">${(mb.total_received || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-slate-600">${(mb.total_paid_out || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-amber-600">${(mb.pending_payout || 0).toFixed(2)}</td>
                    <td className="px-6 py-4">
                      {mb.current_balance > 0 && onOpenPayoutDialog && (
                        <Button 
                          size="sm" 
                          onClick={() => onOpenPayoutDialog({ 
                            id: mb.merchant_id, 
                            business_name: mb.merchant_name 
                          })}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        >
                          <Banknote className="w-3 h-3 mr-1" />
                          {t('dashboard.admin.createPayout')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payouts List */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="payouts-table">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-600" />
            {t('dashboard.admin.payouts')}
          </h3>
          {stats?.pending_payouts > 0 && (
            <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
              {stats.pending_payouts} {t('dashboard.status.pending')}
            </span>
          )}
        </div>
        {payouts.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Banknote className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{t('tables.noPayoutsYet')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.payoutId')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.colmado')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.gross')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.fees')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('tables.net')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payouts.map((payout) => (
                  <tr key={payout.id || payout.payout_id} className="hover:bg-slate-50" data-testid={`payout-${payout.payout_id}`}>
                    <td className="px-6 py-4 font-mono text-xs text-slate-900">{payout.payout_id}</td>
                    <td className="px-6 py-4 text-slate-900 text-sm">{payout.merchant_name}</td>
                    <td className="px-6 py-4 text-slate-600 text-sm">${(payout.gross_amount || payout.total_amount)?.toFixed(2)}</td>
                    <td className="px-6 py-4 text-red-600 text-sm">-${(payout.fees_amount || 0)?.toFixed(2)}</td>
                    <td className="px-6 py-4 font-semibold text-emerald-600">${(payout.net_amount || payout.total_amount)?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(payout.status)}`}>
                        {getStatusIcon(payout.status)}
                        {payout.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 flex-wrap">
                        {/* Draft -> Submit for Approval */}
                        {payout.status === 'draft' && onPayoutAction && (
                          <Button size="sm" onClick={() => onPayoutAction(payout.payout_id, 'submit')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                            Submit
                          </Button>
                        )}
                        {/* Pending Approval or Draft -> Approve */}
                        {(payout.status === 'pending_approval' || payout.status === 'pending' || payout.status === 'draft') && onPayoutAction && (
                          <Button size="sm" onClick={() => onPayoutAction(payout.payout_id, 'approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                            Approve
                          </Button>
                        )}
                        {/* Approved -> Processing */}
                        {payout.status === 'approved' && onPayoutAction && (
                          <Button size="sm" onClick={() => onPayoutAction(payout.payout_id, 'processing')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                            <Play className="w-3 h-3 mr-1" />
                            Start
                          </Button>
                        )}
                        {/* Processing or Approved -> Complete */}
                        {(payout.status === 'processing' || payout.status === 'approved') && onPayoutAction && (
                          <Button size="sm" onClick={() => onPayoutAction(payout.payout_id, 'complete')} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Button>
                        )}
                        {/* Cancel (before processing) */}
                        {['draft', 'pending_approval', 'pending', 'approved'].includes(payout.status) && onPayoutAction && (
                          <Button size="sm" variant="outline" onClick={() => onPayoutAction(payout.payout_id, 'cancel')} className="text-red-600 border-red-200 text-xs">
                            Cancel
                          </Button>
                        )}
                        {/* Export CSV */}
                        {onExportPayout && (
                          <Button size="sm" variant="ghost" onClick={() => onExportPayout(payout.payout_id)} title="Export CSV">
                            <Download className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default PayoutsTab;
